import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const LOCATION_REQUIRED_MSG = "Location access is required to use Luxury Rideshare — please turn on location";
const IDLE_MS = 15000;
const TRIP_MS = 7000;

const Ctx = createContext({ status: "idle", sendNow: async () => false, setTripActive: () => {}, retry: () => {} });

// Foreground-only customer GPS: shares while the tab is visible, stops when hidden or signed out.
export function CustomerLocationProvider({ children }) {
  const { user } = useAuth();
  const enabled = Boolean(user && user.role === "customer" && user.gps_consent);
  const [status, setStatus] = useState("idle");
  const [visible, setVisible] = useState(document.visibilityState === "visible");
  const [tripActive, setTripActive] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const pos = useRef(null);

  const sendNow = useCallback(async () => {
    if (!pos.current) return false;
    try { await api.post("/customer/location", pos.current); return true; } catch { return false; }
  }, []);

  useEffect(() => {
    const f = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", f);
    return () => document.removeEventListener("visibilitychange", f);
  }, []);

  useEffect(() => {
    if (!enabled) { pos.current = null; setStatus("idle"); return undefined; }
    if (!navigator.geolocation) { setStatus("unavailable"); return undefined; }
    if (!visible) return undefined;
    setStatus((s) => (s === "on" ? s : "pending"));
    let first = true;
    const watch = navigator.geolocation.watchPosition(
      (p) => {
        pos.current = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, ts: new Date(p.timestamp).toISOString() };
        setStatus("on");
        if (first) { first = false; sendNow(); }
      },
      (e) => setStatus(e.code === 1 ? "denied" : pos.current ? "on" : "unavailable"),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    const t = setInterval(sendNow, tripActive ? TRIP_MS : IDLE_MS);
    return () => { clearInterval(t); navigator.geolocation.clearWatch(watch); };
  }, [enabled, visible, tripActive, attempt, sendNow]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return <Ctx.Provider value={{ status, sendNow, setTripActive, retry }}>{children}</Ctx.Provider>;
}

export const useCustomerLocation = () => useContext(Ctx);
